import { SupermarketTier } from "../data/supermarkets";
import { estimateAnnualCouncilTax } from "../data/londonCouncilTaxBands";

const GROCERY_BANDS: Record<
  SupermarketTier,
  { min: number; max: number }
> = {
  premium: { min: 110, max: 140 },
  mid: { min: 70, max: 90 },
  value: { min: 45, max: 60 },
};

export function parseCouncilTaxFromDescription(
  description: string[]
): number | null {
  const text = description.join(" ");
  const annualMatch = text.match(
    /council\s*tax[:\s]*£?\s*([\d,]+(?:\.\d+)?)\s*(?:per\s*year|p\.?a\.?|annually)?/i
  );
  if (annualMatch) {
    return parseFloat(annualMatch[1].replace(/,/g, ""));
  }
  const monthlyMatch = text.match(
    /council\s*tax[:\s]*£?\s*([\d,]+(?:\.\d+)?)\s*(?:per\s*month|pcm|p\.?m\.?)/i
  );
  if (monthlyMatch) {
    return parseFloat(monthlyMatch[1].replace(/,/g, "")) * 12;
  }
  return null;
}

export function estimateCouncilTaxMonthly(
  postcode: string,
  description: string[] = [],
  occupants = 1
): number {
  const parsedAnnual = parseCouncilTaxFromDescription(description);
  const annual =
    parsedAnnual ?? estimateAnnualCouncilTax(postcode);
  const monthly = annual / 12;
  const discount = occupants === 1 ? 0.75 : 1;
  return Math.round(monthly * discount);
}

export function estimateUtilities(
  bedrooms: number,
  internalArea: number | null
): number {
  const beds = Math.max(1, bedrooms || 1);
  const area = internalArea && internalArea > 0 ? internalArea : beds * 35;
  const base = 90 + beds * 25 + Math.min(area, 120) * 0.35;
  return Math.round(base);
}

export function estimateGroceries(
  tier: SupermarketTier,
  occupants: number,
  shopsPerWeek = 1
): { weeklyMin: number; weeklyMax: number; monthlyMin: number; monthlyMax: number } {
  const band = GROCERY_BANDS[tier];
  const multiplier = Math.max(1, occupants) * Math.max(1, shopsPerWeek * 0.85);
  const weeklyMin = Math.round(band.min * multiplier);
  const weeklyMax = Math.round(band.max * multiplier);
  return {
    weeklyMin,
    weeklyMax,
    monthlyMin: Math.round(weeklyMin * 4.33),
    monthlyMax: Math.round(weeklyMax * 4.33),
  };
}

export function dominantGroceryTier(
  tiers: SupermarketTier[]
): SupermarketTier {
  if (tiers.length === 0) return "mid";
  const score = { premium: 0, mid: 0, value: 0 };
  for (const tier of tiers) score[tier] += 1;
  if (score.premium >= score.mid && score.premium >= score.value) {
    return "premium";
  }
  if (score.value > score.mid) return "value";
  return "mid";
}
